import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Activity, Loader2, CheckCircle } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { ROUTES } from '@/lib/routes';

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (password !== confirm) {
      setError('Les mots de passe ne correspondent pas.');
      return;
    }
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await apiClient.post('/v1/auth/set-password', { token, password });
      setDone(true);
      setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 3000);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
          ?.message ?? 'Lien invalide ou expiré.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-dark via-[#1e3a2e] to-[#2a4a3a] flex items-center justify-center p-4">
        <div className="card p-8 max-w-md w-full text-center space-y-4">
          <p className="text-sm text-red-600">Lien invalide — le token est manquant.</p>
          <Link to={ROUTES.LOGIN} className="btn-primary inline-flex">
            Retour à la connexion
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-dark via-[#1e3a2e] to-[#2a4a3a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-brand bg-brand-primary mb-4 shadow-lg">
            <Activity size={26} className="text-white" />
          </div>
          <h1 className="font-heading text-2xl font-bold text-white">Dispatcher</h1>
          <p className="text-green-200/60 text-sm mt-1">Définir votre mot de passe</p>
        </div>

        <div className="card p-8 shadow-xl">
          {done ? (
            <div className="text-center space-y-4">
              <CheckCircle className="mx-auto text-brand-primary" size={40} />
              <h2 className="font-heading text-lg font-bold text-brand-dark">Mot de passe défini</h2>
              <p className="text-sm text-gray-500">Redirection vers la connexion…</p>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-lg font-bold text-brand-dark mb-2">
                Définir votre mot de passe
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Choisissez un mot de passe sécurisé pour activer votre compte.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="label" htmlFor="password">Nouveau mot de passe</label>
                  <input
                    id="password"
                    type="password"
                    className="input"
                    placeholder="8 caractères minimum"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    autoComplete="new-password"
                  />
                </div>

                <div>
                  <label className="label" htmlFor="confirm">Confirmer le mot de passe</label>
                  <input
                    id="confirm"
                    type="password"
                    className="input"
                    placeholder="••••••••"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    autoComplete="new-password"
                  />
                </div>

                {error && (
                  <div role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-brand px-3 py-2">
                    {error}
                  </div>
                )}

                <button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading && <Loader2 size={14} className="animate-spin" />}
                  Définir le mot de passe
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
